import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useIsMobile } from '../components/Layout'
import { formatDate, toDateStr } from '../lib/dates'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const t = {
  green800:  '#1a3d1a',
  green700:  '#1e4d1e',
  green600:  '#2d6a2d',
  green500:  '#3a8a3a',
  green400:  '#4fa84f',
  green300:  '#7dc47d',
  green100:  '#e8f5e8',
  green50:   '#f2faf2',
  white:     '#ffffff',
  bg:        '#f0f2f0',
  border:    '#e4ebe4',
  text900:   '#1a2e1a',
  text600:   '#3d5c3d',
  text400:   '#6b8a6b',
  text300:   '#9aaf9a',
  shadow:    '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd:  '0 4px 14px rgba(0,0,0,0.09), 0 2px 4px rgba(0,0,0,0.05)',
  shadowHov: '0 8px 24px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.06)',
  radius:    '12px',
  radiusSm:  '8px',
}

export default function Dashboard() {
  const { isAdmin, profile } = useAuth()
  const isMobile = useIsMobile()
  const [stats, setStats]               = useState({ activeJobs: 0, totalClients: 0, openEstimates: 0, monthRevenue: 0 })
  const [overdueInvoices, setOverdueInvoices]   = useState([])
  const [pendingEstimates, setPendingEstimates] = useState([])
  const [pendingProposals, setPendingProposals] = useState([])
  const [uninvoicedJobs, setUninvoicedJobs]     = useState([])
  const [dueReminders, setDueReminders]         = useState([])
  const [todaySiteVisits, setTodaySiteVisits]   = useState([])
  const [proposalStats, setProposalStats]       = useState({ decided: 0, approved: 0, avgDays: null })
  const [todayJobs, setTodayJobs]               = useState([])
  const [incompleteJobs, setIncompleteJobs]     = useState([])
  const [clockedInNow, setClockedInNow]         = useState([])
  const [myClockedIn, setMyClockedIn]           = useState(null)
  const [loading, setLoading]                   = useState(true)

  useEffect(() => { if (profile?.id) fetchData() }, [profile?.id])

  async function fetchData() {
    try {
      const now          = new Date()
      const todayStr     = toDateStr(now)
      const monthStart   = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
      const monthEnd     = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]

      const basePromises = [
        // My active clock entry
        supabase.from('time_entries')
          .select('*, job:jobs(title)')
          .eq('employee_id', profile?.id)
          .is('clock_out', null)
          .maybeSingle(),
      ]

      if (isAdmin) {
        basePromises.push(
          // Total clients count
          supabase.from('clients').select('id', { count: 'exact', head: true }),
          // Open estimates count
          supabase.from('estimates').select('id', { count: 'exact', head: true }).in('status', ['draft', 'sent']),
          // Month revenue from completed jobs — bucketed by scheduled_date (not created_at)
          supabase.from('jobs').select('contract_amount').eq('status', 'completed').gte('scheduled_date', monthStart).lte('scheduled_date', monthEnd),
          // Active jobs count
          supabase.from('jobs').select('id', { count: 'exact', head: true }).eq('status', 'active'),
          // Overdue invoices — status sent and due_date < today
          supabase.from('invoices')
            .select('id, invoice_number, amount, due_date, client:clients(name)')
            .eq('status', 'sent')
            .lt('due_date', todayStr)
            .order('due_date', { ascending: true })
            .limit(5),
          // Estimates awaiting approval — sent more than 1 day ago
          supabase.from('estimates')
            .select('id, created_at, total_cost, client:clients(name)')
            .eq('status', 'sent')
            .order('created_at', { ascending: true })
            .limit(5),
          // Proposals awaiting a response (5.4) — the customer-facing object now,
          // separate signal from the estimates-sent check above which predates the
          // proposal workflow (Phase 3). Checked against non-terminal status rather
          // than specifically 'sent'/'viewed': nothing in the app currently marks a
          // proposal as sent (no send feature exists yet, see Docs/DECISIONS_NEEDED.md
          // history), so every proposal sits in 'draft' until approved/declined --
          // this stays correct today AND once send-tracking eventually gets built.
          supabase.from('proposals')
            .select('id, proposal_number, created_at, selected_contract_total, client:clients(name)')
            .not('status', 'in', '(approved,declined,expired,superseded)')
            .eq('is_current_version', true)
            .order('created_at', { ascending: true })
            .limit(5),
          // Jobs scheduled today
          supabase.from('jobs')
            .select('id, title, start_time, client:clients(name), status')
            .eq('scheduled_date', todayStr)
            .neq('status', 'completed')
            .neq('status', 'cancelled')
            .order('start_time', { ascending: true }),
          // Jobs missing contract amount (active or pending, no contract)
          supabase.from('jobs')
            .select('id, title, client:clients(name), status')
            .in('status', ['active', 'pending'])
            .or('contract_amount.is.null,contract_amount.eq.0')
            .limit(5),
          // Employees currently clocked in
          supabase.from('time_entries')
            .select('id, clock_in, employee:profiles(full_name), job:jobs(title)')
            .is('clock_out', null)
            .order('clock_in', { ascending: true }),
          // Follow-up reminders due today or overdue
          supabase.from('reminders')
            .select('id, title, remind_on, client_id, job_id, client:clients(name), job:jobs(title)')
            .eq('status', 'open')
            .lte('remind_on', todayStr)
            .order('remind_on', { ascending: true })
            .limit(5),
          // Site visits scheduled today
          supabase.from('site_visits')
            .select('id, title, start_time, client:clients(name), assigned:profiles!site_visits_assigned_to_fkey(full_name)')
            .eq('scheduled_date', todayStr)
            .eq('status', 'Open')
            .order('start_time', { ascending: true }),
          // Proposal performance (TD-028) -- current-version proposals only, so a
          // superseded draft doesn't get double-counted alongside its replacement.
          supabase.from('proposals')
            .select('status, created_at, approved_at, declined_at')
            .eq('is_current_version', true)
            .in('status', ['approved', 'declined']),
        )
      }

      const [clockRes, ...adminRes] = await Promise.all(basePromises)
      if (clockRes.data) setMyClockedIn(clockRes.data)

      if (isAdmin) {
        const [
          clientsRes, estimatesRes, monthJobsRes, activeJobsRes,
          overdueRes, pendingEstRes, pendingPropRes, todayRes, incompleteRes, clockedInRes, dueRemindersRes, todaySiteVisitsRes, decidedProposalsRes
        ] = adminRes

        const monthRevenue = (monthJobsRes?.data || []).reduce((s, j) => s + (j.contract_amount || 0), 0)

        setStats({
          activeJobs:    activeJobsRes?.count || 0,
          totalClients:  clientsRes?.count || 0,
          openEstimates: estimatesRes?.count || 0,
          monthRevenue,
        })
        setOverdueInvoices(overdueRes?.data || [])
        setPendingEstimates(pendingEstRes?.data || [])
        setPendingProposals(pendingPropRes?.data || [])
        setTodayJobs(todayRes?.data || [])
        setIncompleteJobs(incompleteRes?.data || [])
        setClockedInNow(clockedInRes?.data || [])
        setDueReminders(dueRemindersRes?.data || [])
        setTodaySiteVisits(todaySiteVisitsRes?.data || [])

        // Proposal performance (TD-028) -- win rate + avg days-to-decision over
        // every current-version proposal that's actually been decided (approved or
        // declined). Proposals still awaiting a response don't count toward either
        // side yet -- they're not a loss, they're pending (already surfaced
        // separately via the "Proposals Awaiting Response" card above).
        const decided = decidedProposalsRes?.data || []
        const approvedCount = decided.filter(p => p.status === 'approved').length
        const daysToDecision = decided
          .map(p => {
            const decidedAt = p.approved_at || p.declined_at
            if (!decidedAt || !p.created_at) return null
            return (new Date(decidedAt) - new Date(p.created_at)) / 86400000
          })
          .filter(d => d != null && d >= 0)
        const avgDays = daysToDecision.length > 0 ? daysToDecision.reduce((s, d) => s + d, 0) / daysToDecision.length : null
        setProposalStats({ decided: decided.length, approved: approvedCount, avgDays })

        // Jobs completed but not yet invoiced (5.4) -- needs a second, dependent
        // query (which jobs have zero invoices) so it can't join the parallel batch
        // above. Scoped to recent completions only, not the full job history.
        const { data: recentCompleted } = await supabase
          .from('jobs')
          .select('id, title, client:clients(name), scheduled_date')
          .eq('status', 'completed')
          .order('scheduled_date', { ascending: false })
          .limit(15)
        if (recentCompleted?.length > 0) {
          const { data: theirInvoices } = await supabase
            .from('invoices')
            .select('job_id')
            .in('job_id', recentCompleted.map(j => j.id))
          const invoicedJobIds = new Set((theirInvoices || []).map(i => i.job_id))
          setUninvoicedJobs(recentCompleted.filter(j => !invoicedJobIds.has(j.id)).slice(0, 5))
        } else {
          setUninvoicedJobs([])
        }
      }
    } catch (e) {
      console.error('Dashboard fetch error:', e) // allowed-debug — intentional error-path logging
    }
    setLoading(false)
  }

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const daysSince = (iso) => {
    const diff = Date.now() - new Date(iso).getTime()
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  const daysOverdue = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  // Total attention items for admin
  const attentionCount = overdueInvoices.length + pendingEstimates.length + pendingProposals.length + incompleteJobs.length + uninvoicedJobs.length + dueReminders.length

  // Extracted so it can render second on mobile (right after the action tiles)
  // but stay in its normal spot within the two-col layout on desktop.
  const todaysScheduleCard = (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <div style={s.cardHeaderLeft}>
          <div style={{ ...s.cardAccentBar, background: '#0369a1' }} />
          <h2 style={s.cardTitle}>Today's Schedule</h2>
        </div>
        <Link to="/schedule" style={s.viewAllLink}>View Schedule →</Link>
      </div>
      {loading ? <LoadingRows /> : todayJobs.length === 0 ? (
        <div style={s.emptySmall}>No jobs scheduled for today</div>
      ) : (
        <div>
          {todayJobs.map((job, i) => (
            <Link key={job.id} to={`/jobs/${job.id}`} style={{ ...s.briefingRow, borderBottom: i < todayJobs.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
              <div style={s.briefingLeft}>
                <span style={{ ...s.briefingDot, background: '#0369a1' }} />
                <div>
                  <div style={s.briefingTitle}>{job.title}</div>
                  <div style={s.briefingMeta}>{job.client?.name || 'No client'}{job.start_time ? ` · ${job.start_time}` : ''}</div>
                </div>
              </div>
              <span style={{ ...s.pill, color: '#0369a1', background: '#e0f2fe' }}>Scheduled</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )

  const todaySiteVisitsCard = (todaySiteVisits.length > 0 || loading) && (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <div style={s.cardHeaderLeft}>
          <div style={{ ...s.cardAccentBar, background: '#7c3aed' }} />
          <h2 style={s.cardTitle}>Site Visits Today</h2>
          {todaySiteVisits.length > 0 && <span style={{ ...s.pill, color: '#7c3aed', background: '#ede9fe', marginLeft: '6px' }}>{todaySiteVisits.length}</span>}
        </div>
        <Link to="/site-visits" style={s.viewAllLink}>View All →</Link>
      </div>
      {loading ? <LoadingRows /> : (
        <div>
          {todaySiteVisits.map((v, i) => (
            <Link key={v.id} to={`/site-visits/${v.id}`} style={{ ...s.briefingRow, borderBottom: i < todaySiteVisits.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
              <div style={s.briefingLeft}>
                <span style={{ ...s.briefingDot, background: '#7c3aed' }} />
                <div>
                  <div style={s.briefingTitle}>{v.title || v.client?.name || 'Site Visit'}</div>
                  <div style={s.briefingMeta}>{v.client?.name || 'No client'}{v.start_time ? ` · ${v.start_time}` : ''}{v.assigned?.full_name ? ` · ${v.assigned.full_name}` : ''}</div>
                </div>
              </div>
              <span style={{ ...s.pill, color: '#7c3aed', background: '#ede9fe' }}>Site Visit</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div style={s.page}>

      {/* ── Header ── */}
      <div style={{ ...s.pageHeader, ...(isMobile ? { flexDirection: 'column', alignItems: 'stretch', gap: '12px' } : {}) }}>
        <div>
          <h1 style={s.pageTitle}>
            {greeting()}, {profile?.full_name?.split(' ')[0] || 'there'}
          </h1>
          <p style={s.pageDate}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div style={s.headerRight}>
          {!isAdmin && myClockedIn && (
            <div style={s.clockBadge}>
              <span style={s.clockDot} />
              Clocked In — {myClockedIn.job?.title}
            </div>
          )}
          {isAdmin && (
            <Link to="/jobs/new" style={s.newJobBtn}>+ New Job</Link>
          )}
        </div>
      </div>

      {/* ── Mobile: actions first, data below. Quick Actions further down is
          desktop-only now — these tiles replace it on mobile, not duplicate it. ── */}
      {isAdmin && isMobile && (
        <div style={s.heroActions}>
          <ActionTile to="/jobs/new"       icon="🔨" label="New Job"      accent={t.green600} big />
          <ActionTile to="/estimates/new"  icon="📋" label="New Estimate" accent="#92400e"     big />
          <ActionTile to="/clients/new"    icon="👥" label="Add Client"   accent="#065f46"     big />
          <ActionTile to="/clock"          icon="⏱️" label="Clock In/Out" accent="#1e3a5f"     big />
        </div>
      )}

      {/* Second on mobile, per John: actions, then today's schedule, then everything else */}
      {isAdmin && isMobile && (
        <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {todaysScheduleCard}
          {todaySiteVisitsCard}
        </div>
      )}

      {/* ── Stat cards (admin only) ── */}
      {isAdmin && (
        <div style={{ ...s.statsRow, ...(isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}) }}>
          <StatCard label="Active Jobs"     value={stats.activeJobs}                             icon="🔨" iconBg="#e8f5e8" link="/jobs"       linkLabel="View all →" accent={t.green600} />
          <StatCard label="Total Clients"   value={stats.totalClients}                           icon="👥" iconBg="#e8f0ff" link="/clients"    linkLabel="View all →" accent="#2563eb" />
          <StatCard label="Open Estimates"  value={stats.openEstimates}                          icon="📋" iconBg="#fff7e8" link="/estimates"  linkLabel="View all →" accent="#d97706" />
          <StatCard label="Month Revenue"   value={`$${stats.monthRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} icon="💰" iconBg="#f3e8ff" link="/financials" linkLabel="View P&L →" accent="#7c3aed" />
          <StatCard
            label="Proposal Win Rate"
            value={proposalStats.decided === 0 ? '—' : `${Math.round((proposalStats.approved / proposalStats.decided) * 100)}%`}
            icon="🎯" iconBg="#fce7f3" link="/proposals"
            linkLabel={proposalStats.avgDays != null ? `Avg ${proposalStats.avgDays.toFixed(1)}d to decide →` : 'View proposals →'}
            accent="#be185d"
          />
        </div>
      )}

      {/* ── Employee view — assigned jobs + site visits ── */}
      {!isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div style={s.cardHeaderLeft}>
                <div style={{ ...s.cardAccentBar, background: '#7c3aed' }} />
                <h2 style={s.cardTitle}>My Site Visits Today</h2>
              </div>
              <Link to="/site-visits" style={s.viewAllLink}>View All →</Link>
            </div>
            <EmployeeSiteVisitsToday profileId={profile?.id} />
          </div>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div style={s.cardHeaderLeft}>
                <div style={s.cardAccentBar} />
                <h2 style={s.cardTitle}>My Assigned Jobs</h2>
              </div>
              <Link to="/jobs" style={s.viewAllLink}>View All →</Link>
            </div>
            <EmployeeJobList profileId={profile?.id} />
          </div>
        </div>
      )}

      {/* ── Admin two-col layout ── */}
      {isAdmin && (
        <div style={{ ...s.twoCol, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>

          {/* Left col — Today's Briefing */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Today's Jobs — shown up top on mobile instead (see above); desktop keeps it here */}
            {!isMobile && todaysScheduleCard}
            {!isMobile && todaySiteVisitsCard}

            {/* Overdue Invoices */}
            {(loading || overdueInvoices.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#dc2626' }} />
                    <h2 style={s.cardTitle}>Overdue Invoices</h2>
                    {overdueInvoices.length > 0 && <span style={{ ...s.pill, color: '#dc2626', background: '#fee2e2', marginLeft: '6px' }}>{overdueInvoices.length}</span>}
                  </div>
                  <Link to="/invoices" style={s.viewAllLink}>View All →</Link>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {overdueInvoices.map((inv, i) => (
                      <Link key={inv.id} to="/invoices" style={{ ...s.briefingRow, borderBottom: i < overdueInvoices.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#dc2626' }} />
                          <div>
                            <div style={s.briefingTitle}>{inv.client?.name || 'Unknown client'}</div>
                            <div style={s.briefingMeta}>{inv.invoice_number} · {daysOverdue(inv.due_date)} days overdue</div>
                          </div>
                        </div>
                        <span style={s.briefingAmount}>${Number(inv.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Estimates Awaiting Approval */}
            {(loading || pendingEstimates.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#d97706' }} />
                    <h2 style={s.cardTitle}>Awaiting Approval</h2>
                    {pendingEstimates.length > 0 && <span style={{ ...s.pill, color: '#d97706', background: '#fef3c7', marginLeft: '6px' }}>{pendingEstimates.length}</span>}
                  </div>
                  <Link to="/estimates" style={s.viewAllLink}>View All →</Link>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {pendingEstimates.map((est, i) => (
                      <Link key={est.id} to="/estimates" style={{ ...s.briefingRow, borderBottom: i < pendingEstimates.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#d97706' }} />
                          <div>
                            <div style={s.briefingTitle}>{est.client?.name || 'Unknown client'}</div>
                            <div style={s.briefingMeta}>Sent {daysSince(est.created_at)} days ago</div>
                          </div>
                        </div>
                        {est.total_cost != null && (
                          <span style={s.briefingAmount}>${Number(est.total_cost).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Proposals Awaiting Response (5.4) */}
            {(loading || pendingProposals.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#7c3aed' }} />
                    <h2 style={s.cardTitle}>Proposals Awaiting Response</h2>
                    {pendingProposals.length > 0 && <span style={{ ...s.pill, color: '#7c3aed', background: '#ede9fe', marginLeft: '6px' }}>{pendingProposals.length}</span>}
                  </div>
                  <Link to="/proposals" style={s.viewAllLink}>View All →</Link>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {pendingProposals.map((prop, i) => (
                      <Link key={prop.id} to={`/proposals/${prop.id}`} style={{ ...s.briefingRow, borderBottom: i < pendingProposals.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#7c3aed' }} />
                          <div>
                            <div style={s.briefingTitle}>{prop.client?.name || 'Unknown client'}</div>
                            <div style={s.briefingMeta}>{prop.proposal_number} · {daysSince(prop.created_at)} days ago</div>
                          </div>
                        </div>
                        <span style={s.briefingAmount}>${Number(prop.selected_contract_total || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Jobs Missing Info */}
            {(loading || incompleteJobs.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#6b7280' }} />
                    <h2 style={s.cardTitle}>Jobs Missing Contract Amount</h2>
                    {incompleteJobs.length > 0 && <span style={{ ...s.pill, color: '#6b7280', background: '#f3f4f6', marginLeft: '6px' }}>{incompleteJobs.length}</span>}
                  </div>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {incompleteJobs.map((job, i) => (
                      <Link key={job.id} to={`/jobs/${job.id}`} style={{ ...s.briefingRow, borderBottom: i < incompleteJobs.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#9ca3af' }} />
                          <div>
                            <div style={s.briefingTitle}>{job.title}</div>
                            <div style={s.briefingMeta}>{job.client?.name || 'No client'} · {job.status}</div>
                          </div>
                        </div>
                        <span style={{ ...s.pill, color: '#6b7280', background: '#f3f4f6' }}>No amount</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Completed, Not Yet Invoiced (5.4) */}
            {(loading || uninvoicedJobs.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#0369a1' }} />
                    <h2 style={s.cardTitle}>Completed, Not Yet Invoiced</h2>
                    {uninvoicedJobs.length > 0 && <span style={{ ...s.pill, color: '#0369a1', background: '#e0f2fe', marginLeft: '6px' }}>{uninvoicedJobs.length}</span>}
                  </div>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {uninvoicedJobs.map((job, i) => (
                      <Link key={job.id} to={`/jobs/${job.id}`} style={{ ...s.briefingRow, borderBottom: i < uninvoicedJobs.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#0369a1' }} />
                          <div>
                            <div style={s.briefingTitle}>{job.title}</div>
                            <div style={s.briefingMeta}>{job.client?.name || 'No client'}</div>
                          </div>
                        </div>
                        <span style={{ ...s.pill, color: '#0369a1', background: '#e0f2fe' }}>No invoice</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Follow-ups Due */}
            {(loading || dueReminders.length > 0) && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#be185d' }} />
                    <h2 style={s.cardTitle}>Follow-ups Due</h2>
                    {dueReminders.length > 0 && <span style={{ ...s.pill, color: '#be185d', background: '#fce7f3', marginLeft: '6px' }}>{dueReminders.length}</span>}
                  </div>
                </div>
                {loading ? <LoadingRows count={2} /> : (
                  <div>
                    {dueReminders.map((r, i) => (
                      <Link key={r.id} to={r.job_id ? `/jobs/${r.job_id}` : `/clients/${r.client_id}`} style={{ ...s.briefingRow, borderBottom: i < dueReminders.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#be185d' }} />
                          <div>
                            <div style={s.briefingTitle}>{r.title}</div>
                            <div style={s.briefingMeta}>{r.job?.title || r.client?.name || 'Unknown'} · {formatDate(r.remind_on)}</div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* All clear message */}
            {!loading && attentionCount === 0 && todayJobs.length === 0 && (
              <div style={{ ...s.card, padding: '28px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                <div style={{ fontWeight: '700', color: t.text900, marginBottom: '4px' }}>All clear</div>
                <div style={{ fontSize: '13px', color: t.text400 }}>No overdue invoices, pending estimates, incomplete jobs, or follow-ups due</div>
              </div>
            )}
          </div>

          {/* Right col — Live crew + Quick Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Live Crew Status */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.cardHeaderLeft}>
                  <div style={{ ...s.cardAccentBar, background: '#16a34a' }} />
                  <h2 style={s.cardTitle}>Crew Status</h2>
                  {clockedInNow.length > 0 && (
                    <span style={{ ...s.pill, color: '#16a34a', background: '#dcfce7', marginLeft: '6px' }}>
                      {clockedInNow.length} active
                    </span>
                  )}
                </div>
                <Link to="/clock" style={s.viewAllLink}>Time Cards →</Link>
              </div>
              {loading ? <LoadingRows count={2} /> : clockedInNow.length === 0 ? (
                <div style={s.emptySmall}>No crew currently clocked in</div>
              ) : (
                <div>
                  {clockedInNow.map((entry, i) => {
                    const elapsed = Math.floor((Date.now() - new Date(entry.clock_in)) / 1000)
                    const h = Math.floor(elapsed / 3600)
                    const m = Math.floor((elapsed % 3600) / 60)
                    return (
                      <div key={entry.id} style={{ ...s.briefingRow, borderBottom: i < clockedInNow.length - 1 ? `1px solid ${t.border}` : 'none' }}>
                        <div style={s.briefingLeft}>
                          <span style={{ ...s.briefingDot, background: '#16a34a' }} />
                          <div>
                            <div style={s.briefingTitle}>{entry.employee?.full_name || '—'}</div>
                            <div style={s.briefingMeta}>{entry.job?.title || 'No job'}</div>
                          </div>
                        </div>
                        <span style={{ ...s.pill, color: '#16a34a', background: '#dcfce7' }}>{h}h {m}m</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Quick Actions — desktop only. Mobile has the hero tiles up top instead
                (John, 2026-07-12: confirmed redundant once the hero tiles shipped). */}
            {!isMobile && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.cardAccentBar, background: '#2563eb' }} />
                    <h2 style={s.cardTitle}>Quick Actions</h2>
                  </div>
                </div>
                <div style={s.actionsGrid}>
                  <ActionTile to="/jobs/new"       icon="🔨" label="New Job"      accent={t.green600} />
                  <ActionTile to="/clock"           icon="⏱️" label="Clock In/Out" accent="#1e3a5f" />
                  {isAdmin && <ActionTile to="/clients/new"   icon="👥" label="Add Client"   accent="#065f46" />}
                  {isAdmin && <ActionTile to="/estimates/new" icon="📋" label="New Estimate" accent="#92400e" />}
                  {isAdmin && <ActionTile to="/financials"    icon="💰" label="Financials"   accent="#5b21b6" />}
                  {isAdmin && <ActionTile to="/schedule"      icon="📅" label="Schedule"     accent="#0369a1" />}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  )
}

// ─── Employee assigned jobs list ───────────────────────────────────────────────
function EmployeeSiteVisitsToday({ profileId }) {
  const [visits, setVisits] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      if (!profileId) return
      const todayStr = toDateStr(new Date())
      const { data } = await supabase
        .from('site_visits')
        .select('id, title, start_time, client:clients(name)')
        .eq('assigned_to', profileId)
        .eq('scheduled_date', todayStr)
        .eq('status', 'Open')
        .order('start_time', { ascending: true })
      setVisits(data || [])
      setLoading(false)
    }
    load()
  }, [profileId])

  if (loading) return <LoadingRows count={2} />
  if (visits.length === 0) return <div style={{ padding: '24px 20px', color: t.text400, fontSize: '14px' }}>No site visits scheduled for you today.</div>

  return (
    <div>
      {visits.map((v, i) => (
        <Link key={v.id} to={`/site-visits/${v.id}`} style={{ ...s.briefingRow, borderBottom: i < visits.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
          <div style={s.briefingLeft}>
            <span style={{ ...s.briefingDot, background: '#7c3aed' }} />
            <div>
              <div style={s.briefingTitle}>{v.title || v.client?.name || 'Site Visit'}</div>
              <div style={s.briefingMeta}>{v.client?.name || 'No client'}{v.start_time ? ` · ${v.start_time}` : ''}</div>
            </div>
          </div>
          <span style={{ ...s.pill, color: '#7c3aed', background: '#ede9fe' }}>Site Visit</span>
        </Link>
      ))}
    </div>
  )
}

function EmployeeJobList({ profileId }) {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      if (!profileId) return
      const { data } = await supabase
        .from('job_assignments')
        .select('job:jobs(id, title, status, scheduled_date, client:clients(name))')
        .eq('employee_id', profileId)
      const active = (data || []).map(a => a.job).filter(j => j && j.status !== 'completed' && j.status !== 'cancelled')
      setJobs(active)
      setLoading(false)
    }
    load()
  }, [profileId])

  const sc = { active: { color: '#16a34a', bg: '#dcfce7', label: 'Active' }, pending: { color: '#d97706', bg: '#fef3c7', label: 'Pending' } }

  if (loading) return <LoadingRows />
  if (jobs.length === 0) return <div style={{ padding: '24px 20px', color: t.text400, fontSize: '14px' }}>No active jobs assigned. Contact your manager.</div>

  return (
    <div>
      {jobs.map((job, i) => {
        const c = sc[job.status] || { color: '#6b7280', bg: '#f3f4f6', label: job.status }
        return (
          <Link key={job.id} to={`/jobs/${job.id}`} style={{ ...s.briefingRow, borderBottom: i < jobs.length - 1 ? `1px solid ${t.border}` : 'none', textDecoration: 'none' }}>
            <div style={s.briefingLeft}>
              <span style={{ ...s.briefingDot, background: c.color }} />
              <div>
                <div style={s.briefingTitle}>{job.title}</div>
                <div style={s.briefingMeta}>{job.client?.name || 'No client'}{job.scheduled_date ? ` · ${formatDate(job.scheduled_date)}` : ''}</div>
              </div>
            </div>
            <span style={{ ...s.pill, color: c.color, background: c.bg }}>{c.label}</span>
          </Link>
        )
      })}
    </div>
  )
}

// ─── Loading skeleton rows ─────────────────────────────────────────────────────
function LoadingRows({ count = 3 }) {
  return (
    <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ height: '44px', borderRadius: t.radiusSm, background: 'linear-gradient(90deg, #f0f4f0 25%, #e8ede8 50%, #f0f4f0 75%)', backgroundSize: '200% 100%' }} />
      ))}
    </div>
  )
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, iconBg, link, linkLabel, accent }) {
  const [hover, setHover] = useState(false)
  return (
    <Link
      to={link}
      style={{
        ...s.statCard,
        boxShadow: hover ? t.shadowHov : t.shadow,
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        borderTop: `3px solid ${hover ? accent : t.border}`,
        textDecoration: 'none',
        display: 'block',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={s.statTop}>
        <div style={{ ...s.statIconWrap, background: iconBg }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>{icon}</span>
        </div>
      </div>
      <div style={{ ...s.statValue, color: t.text900 }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
      <span style={{ ...s.statLink, color: accent }}>{linkLabel}</span>
    </Link>
  )
}

// ─── Action Tile ───────────────────────────────────────────────────────────────
function ActionTile({ to, icon, label, accent, big }) {
  const [hover, setHover] = useState(false)
  return (
    <Link
      to={to}
      style={{
        ...s.actionTile,
        ...(big ? s.actionTileBig : {}),
        background: hover ? accent : t.white,
        color: hover ? 'white' : t.text900,
        border: `1.5px solid ${hover ? accent : t.border}`,
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? `0 6px 16px ${accent}35` : t.shadow,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span style={{ fontSize: big ? '28px' : '22px', lineHeight: 1 }}>{icon}</span>
      <span style={{ ...s.actionLabel, ...(big ? s.actionLabelBig : {}) }}>{label}</span>
    </Link>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page:       { width: '100%', paddingBottom: '40px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle:  { margin: 0, fontSize: '24px', fontWeight: '700', color: t.text900, letterSpacing: '-0.3px' },
  pageDate:   { margin: '4px 0 0', color: t.text400, fontSize: '14px' },
  headerRight: { display: 'flex', gap: '12px', alignItems: 'center' },
  newJobBtn:  { background: t.green600, color: 'white', padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', textDecoration: 'none', letterSpacing: '0.2px', boxShadow: `0 2px 6px ${t.green600}40` },
  clockBadge: { display: 'flex', alignItems: 'center', gap: '7px', background: '#dcfce7', color: '#16a34a', padding: '7px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: '1px solid #bbf7d0' },
  clockDot:   { width: '7px', height: '7px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' },

  statsRow:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '20px' },
  statCard:   { background: t.white, borderRadius: t.radius, padding: '18px 20px 16px', border: `1px solid ${t.border}`, borderTop: `3px solid ${t.border}`, transition: 'all 0.2s ease', cursor: 'pointer' },
  statTop:    { marginBottom: '12px' },
  statIconWrap: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statValue:  { fontSize: '28px', fontWeight: '800', lineHeight: 1.1, marginBottom: '4px', letterSpacing: '-0.5px' },
  statLabel:  { fontSize: '12px', fontWeight: '500', color: t.text400, marginBottom: '12px' },
  statLink:   { fontSize: '12px', fontWeight: '600', opacity: 0.85 },

  twoCol:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },

  card:       { background: t.white, borderRadius: t.radius, border: `1px solid ${t.border}`, overflow: 'hidden', boxShadow: t.shadow },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${t.border}` },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  cardAccentBar:  { width: '3px', height: '18px', background: t.green600, borderRadius: '2px' },
  cardTitle:  { margin: 0, fontSize: '14px', fontWeight: '700', color: t.text900 },
  viewAllLink: { fontSize: '12px', fontWeight: '600', color: t.green600, textDecoration: 'none' },

  briefingRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', transition: 'background 0.12s', cursor: 'pointer' },
  briefingLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  briefingDot:  { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  briefingTitle: { fontSize: '13px', fontWeight: '600', color: t.text900 },
  briefingMeta:  { fontSize: '12px', color: t.text400, marginTop: '1px' },
  briefingAmount: { fontSize: '13px', fontWeight: '700', color: t.text900 },

  pill: { fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px' },
  emptySmall: { padding: '20px', fontSize: '13px', color: t.text400, textAlign: 'center' },

  actionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px 20px 18px' },
  actionTile:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '18px 10px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600', transition: 'all 0.18s ease' },
  actionLabel: { fontSize: '12px', fontWeight: '700', letterSpacing: '0.2px', textAlign: 'center' },

  // Mobile "let's get started" hero tiles — bigger, above the fold, actions before data
  heroActions:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' },
  actionTileBig:  { padding: '22px 10px', gap: '9px' },
  actionLabelBig: { fontSize: '13.5px' },
}
